import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import PageLoading from "components/loading/PageLoading";
import BaseActionButton from "ds/components/BaseAction/Button";
import Box from "ds/components/Box";
import Card from "ds/components/Card";
import Toggle from "ds/components/Toggle";
import Typography from "ds/components/Typography";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { GET_AI_SETTINGS } from "shared/Intelligence/gql";
import useIntelligenceSettings from "shared/Intelligence/useIntelligenceSettings";
import { BillingTierFeature } from "types/generated";
import useTierFeature from "views/Account/hooks/useTierFeature";
import { AccountContext } from "views/AccountWrapper";

import { showAiTermsOfUseModal } from "./TermsOfUseModal";
import EnableAiFeatures from "./EnableAiFeatures";
import InfraAssistantCard from "./InfraAssistantCard";
import PageLayout from "./PageLayout";
import { SET_SPACELIFT_INTELLIGENCE_ENABLED } from "./gql";
import styles from "./styles.module.css";

const AISettings = () => {
  const { accountName } = useTypedContext(AccountContext);
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const hasTierSpaceliftIntelligence = useTierFeature(BillingTierFeature.SpaceliftIntelligence);

  useTitle(`Organization Settings · Spacelift Intelligence · ${accountName}`);

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationAi,
  });

  const {
    aiFeaturesTermsAccepted,
    canUseAiFeatures,
    canUseInfraAssistant,
    canUseInfraAssistantBuildMode,
    loading,
    error,
  } = useIntelligenceSettings({ refreshOnMount: true });

  const [setSpaceliftIntelligenceEnabled, { loading: toggleAILoading }] = useMutation(
    SET_SPACELIFT_INTELLIGENCE_ENABLED,
    {
      refetchQueries: [GET_AI_SETTINGS],
      awaitRefetchQueries: true,
    }
  );

  const handleToggleAIFeatures = useCallback(async () => {
    const newEnabled = !canUseAiFeatures;

    try {
      await setSpaceliftIntelligenceEnabled({
        variables: { enabled: newEnabled },
      });
      trackSegmentAnalyticsEvent("AI Features Toggled", {
        status: newEnabled ? "enabled" : "disabled",
      });
      reportSuccess({
        message: newEnabled ? "Spacelift Intelligence enabled" : "Spacelift Intelligence disabled",
      });
    } catch (e) {
      onError(e);
    }
  }, [
    canUseAiFeatures,
    onError,
    reportSuccess,
    setSpaceliftIntelligenceEnabled,
    trackSegmentAnalyticsEvent,
  ]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading) {
    return <PageLoading />;
  }

  if (!aiFeaturesTermsAccepted) {
    return (
      <PageLayout>
        <Box direction="column" gap="x-large" className={styles.container}>
          <Card fullbleed>
            <Box className={styles.onboarding}>
              <EnableAiFeatures
                heading="Enable AI features for your organization"
                description="Makes AI-powered features available to all users in your organization."
                buttonLabel="Enable AI features"
              />
            </Box>
          </Card>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Box direction="column" gap="x-large" className={styles.container}>
        <Card title="AI features" titleTag="h3">
          <Toggle
            variant="switch"
            onChange={handleToggleAIFeatures}
            isSelected={canUseAiFeatures}
            isDisabled={!hasTierSpaceliftIntelligence || toggleAILoading}
            aria-label="Enable Spacelift Intelligence AI features"
          >
            <Box direction="column">
              <Typography tag="p" variant="p-t8">
                Enable Spacelift Intelligence AI features
              </Typography>
              <Typography tag="p" variant="p-body4" color="secondary">
                Makes AI-powered features available to all users in your organization.{" "}
                <BaseActionButton onPress={showAiTermsOfUseModal} className={styles.termsLink}>
                  Terms of Use
                </BaseActionButton>
                .
              </Typography>
            </Box>
          </Toggle>
        </Card>

        {canUseAiFeatures && (
          <InfraAssistantCard
            infraAssistantEnabled={canUseInfraAssistant}
            infraAssistantBuildModeEnabled={canUseInfraAssistantBuildMode}
          />
        )}
      </Box>
    </PageLayout>
  );
};

export default AISettings;
